import React, { useState } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Artist profile CTA: pick one of my Studio projects and invite this artist. */
export default function InviteToStudioButton({ targetUserId, targetName }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const [busy, setBusy] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    try {
      const r = await api.get("/studio/projects");
      setProjects((r.data.projects || []).filter((p) => p.access_role !== "collaborator"));
    } catch { setProjects([]); }
  };

  const invite = async (p) => {
    setBusy(p.project_id);
    try {
      await api.post(`/studio/projects/${p.project_id}/collaborators/invite`, { user_id: targetUserId });
      toast.success(`Invited ${targetName} to “${p.title || "Untitled Session"}”`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    } finally { setBusy(null); }
  };

  return (
    <>
      <button type="button" onClick={openDialog}
        className="h-9 px-3 rounded-full border border-fuchsia-400/50 text-fuchsia-200 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-fuchsia-500/10 active:scale-95 transition-transform"
        data-testid="profile-invite-collab-btn">
        <Mic className="w-3.5 h-3.5" /> Invite to Collaborate
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0b1a] border-fuchsia-500/30 text-white max-w-md" data-testid="profile-invite-collab-dialog">
          <DialogHeader><DialogTitle>Invite {targetName} to a Studio session</DialogTitle></DialogHeader>
          {projects === null && <p className="text-sm text-gray-400">Loading your projects…</p>}
          {projects?.length === 0 && <p className="text-sm text-gray-400" data-testid="profile-invite-no-projects">Create a Studio project first, then invite collaborators.</p>}
          <ul className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(projects || []).map((p) => (
              <li key={p.project_id}>
                <button type="button" disabled={!!busy} onClick={() => invite(p)}
                  className="w-full text-left p-3 rounded-lg border border-white/10 bg-white/[0.03] hover:border-fuchsia-400/50 disabled:opacity-50 flex items-center gap-2"
                  data-testid={`profile-invite-project-${p.project_id}`}>
                  <span className="flex-1 truncate text-sm font-semibold">{p.title || "Untitled Session"}</span>
                  <span className="text-xs text-fuchsia-300 font-bold">{busy === p.project_id ? "Sending…" : "Invite"}</span>
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
